// League context (PLAN-04 S1, REFOCUS §8, §11).
//
// Veteran is a league: every player on a league day faces the same events, so
// choices decide rank rather than luck. That needs one seed per season, shared
// by everyone, with the league day folded into every stream name. Normal mode
// keeps the save's own seed — nobody else is playing that farm.
//
// The action log that goes with it lives in `actionLog.ts`.

import { hashSeed, streamFor, type Rng } from '../core/rng';
import type { GameState } from '../core/types';
import { BUNDLED_SEASON_ID } from './season';
import { dayOf } from './time';

export interface LeagueContext {
  seasonId: string;
  /** Days since this Veteran run began: day 0 is the season's first day. */
  leagueDay: number;
  /** `hashSeed(seasonId)` — identical for every player in the season. */
  seed: number;
}

/**
 * The league a Veteran save plays in, or null in Normal mode.
 *
 * The season id comes from the save's season stamp (PLAN-01 D3). A save with no
 * stamp is playing on the bundled defaults, which is the season the offline
 * fallback would have handed it anyway.
 */
export function leagueContext(state: Readonly<GameState>): LeagueContext | null {
  if (!state.veteran.enabled) return null;
  const seasonId = state.season?.seasonId ?? BUNDLED_SEASON_ID;
  return {
    seasonId,
    leagueDay: Math.max(0, dayOf(state.simHour) - (state.veteran.startedAtDay ?? 0)),
    seed: hashSeed(seasonId),
  };
}

/**
 * The random stream for `name`. Veteran draws from the league seed with the
 * league day in the name, so two players on the same day get the same roll;
 * Normal draws from the save's own seed, exactly as before.
 */
export function rollStream(state: GameState, name: string): Rng {
  const league = leagueContext(state);
  if (!league) return streamFor(state.seed, name);
  return streamFor(league.seed, `${name}:d${league.leagueDay}`);
}
